import React, { useEffect, useMemo, useState } from "react";
import { useEntity } from "@/hooks/useEntity";
import { citationEntityConfig } from "@/entities/Citation";
import { formatCitation } from "@/lib/paperFormats";
import { Button } from "@/components/ui/button";
import { Lightbulb, Loader2, AlertTriangle } from "lucide-react";
import { fetchCitationSuggestions, type CitationSuggestion } from "@/lib/openaiClient";

type CitationSuggestionPanelProps = {
  documentId: number;
  paperFormat: string;
  documentText: string;
  onInsertCitation: (citationText: string) => void;
  referencesOverride?: any[];
};

const CitationSuggestionPanel: React.FC<CitationSuggestionPanelProps> = ({
  documentId,
  paperFormat,
  documentText,
  onInsertCitation,
  referencesOverride,
}) => {
  const { items } = useEntity(citationEntityConfig);
  const sourceItems = referencesOverride ?? items;
  const aiAssistAvailable = Boolean(
    (import.meta.env.VITE_SUPABASE_FUNCTIONS_URL ?? import.meta.env.VITE_SUPABASE_URL) &&
    import.meta.env.VITE_SUPABASE_ANON_KEY
  );
  const [aiSuggestions, setAiSuggestions] = useState<CitationSuggestion[]>([]);
  const [loadingAI, setLoadingAI] = useState(false);
  const [aiError, setAiError] = useState<string | null>(null);

  const fallbackSuggestions = useMemo(() => {
    const lowerDoc = documentText.toLowerCase();
    return sourceItems
      .filter((ref) => ref.documentId === documentId)
      .filter((ref) => {
        const titleMatch = ref.title && lowerDoc.includes(ref.title.toLowerCase());
        const authorMatch = ref.authors && lowerDoc.includes(String(ref.authors).toLowerCase());
        return titleMatch || authorMatch;
      })
      .slice(0, 5);
  }, [sourceItems, documentId, documentText]);

  useEffect(() => {
    if (!aiAssistAvailable || documentText.length < 50) {
      setAiSuggestions([]);
      setAiError(null);
      setLoadingAI(false);
      return;
    }

    let active = true;
    setLoadingAI(true);
    setAiError(null);

    const timer = setTimeout(async () => {
      try {
        const suggestions = await fetchCitationSuggestions({
          documentText,
          paperFormat,
        });
        if (!active) return;
        setAiSuggestions(suggestions);
      } catch (err) {
        if (!active) return;
        console.error("AI citation suggestions failed", err);
        setAiSuggestions([]);
        setAiError(err instanceof Error ? err.message : "Unable to fetch AI suggestions.");
      } finally {
        if (active) setLoadingAI(false);
      }
    }, 400);

    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [aiAssistAvailable, documentText, paperFormat]);

  const usingAI = aiAssistAvailable && documentText.length >= 50;
  const shouldShowFallback = !aiSuggestions.length && fallbackSuggestions.length > 0;

  return (
    <div className="p-4">
      <div className="flex items-center gap-2 mb-3">
        <Lightbulb className="w-5 h-5 text-yellow-400" />
        <h3 className="font-semibold text-white">Citation Suggestions</h3>
      </div>
      {usingAI && (
        <div className="mb-3 text-[11px] text-slate-400">
          Powered by GPT-5 mini suggestions.
        </div>
      )}
      {usingAI && loadingAI && (
        <div className="flex items-center justify-center py-6 text-slate-400">
          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          Generating citation ideas...
        </div>
      )}
      {usingAI && aiError && !loadingAI && (
        <div className="flex items-center gap-2 text-xs text-yellow-300 bg-yellow-500/10 border border-yellow-500/30 rounded p-2 mb-3">
          <AlertTriangle className="w-4 h-4" />
          {aiError}
        </div>
      )}
      {usingAI && aiSuggestions.length > 0 && (
        <div className="space-y-3">
          {aiSuggestions.map((suggestion, idx) => (
            <div key={`${suggestion.title}-${idx}`} className="bg-white/5 p-3 rounded-lg border border-white/10">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-white font-medium">{suggestion.title}</p>
                  <p className="text-xs text-slate-400 mt-1">{suggestion.contextSentence}</p>
                  <p className="text-xs text-slate-300 mt-2">{suggestion.citationText}</p>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => onInsertCitation(suggestion.citationText)}
                >
                  Insert
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
      {(!usingAI || shouldShowFallback) && fallbackSuggestions.length > 0 && (
        <div className={`space-y-3 ${usingAI ? "mt-4" : ""}`}>
          {fallbackSuggestions.map((ref) => (
            <div
              key={ref.id}
              className="bg-white/5 p-3 rounded-lg flex items-center justify-between"
            >
              <div>
                <div className="text-white font-medium">{ref.title}</div>
                <div className="text-xs text-slate-400">
                  {formatCitation(ref, paperFormat)}
                </div>
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={() => onInsertCitation(formatCitation(ref, paperFormat))}
                className="ml-2"
              >
                Insert Citation
              </Button>
            </div>
          ))}
        </div>
      )}
      {!loadingAI && !aiSuggestions.length && fallbackSuggestions.length === 0 && (
        <div className="text-slate-400 text-center py-8">
          No suggestions found.
        </div>
      )}
    </div>
  );
};

export default CitationSuggestionPanel;